import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, MoreHorizontal, Search } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { resizeImageToDataUrl } from "../../lib/image";
import { PriorityTile } from "../../components/primitives";
import { CURRENT_EA_GAME, eaGameLabel, isLegacyEaLink } from "../../lib/ea-game";
import type { Team } from "../../types";

// Candidato restituito da find-ea-club-successor.
type EaSuccessorCandidate = {
  clubId: string;
  name: string;
  platform: string;
  gamesPlayed: number | null;
  members: number;
  common: string[];
  nameMatch: boolean;
  alreadyTaken: boolean;
};

type EaSuccessorResult = {
  recommended: string | null;
  candidates: EaSuccessorCandidate[];
};

async function invokeErrorMessage(invokeError: any): Promise<string> {
  try {
    const ctx: any = invokeError?.context;
    if (ctx?.body) {
      const txt = typeof ctx.body === "string" ? ctx.body : await ctx.body.text?.();
      if (txt) {
        try {
          const j = JSON.parse(txt);
          return j.error || j.message || txt;
        } catch {
          return txt;
        }
      }
    }
  } catch {
    // ignore
  }
  return invokeError?.message || String(invokeError);
}

export function TeamIdentityCard({ team, canEdit }: { team: Team; canEdit: boolean }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [name, setName] = useState(team.name);
  const [abbreviation, setAbbreviation] = useState(team.abbreviation || "");
  const [primaryColor, setPrimaryColor] = useState(team.primary_color || "#005F7E");
  const [logoUrl, setLogoUrl] = useState(team.logo_url || "");
  const queryClientForEa = useQueryClient();
  const [eaClubId, setEaClubId] = useState(team.ea_club_id || "");
  const [eaPlatform, setEaPlatform] = useState(team.ea_platform || "common-gen5");
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [searchQuery, setSearchQuery] = useState(team.name || "");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<
    Array<{
      clubId: string;
      name: string;
      platform?: string;
      currentDivision?: string | null;
      gamesPlayed?: number | null;
      wins?: number | null;
      losses?: number | null;
      alreadyTaken?: boolean;
    }>
  >([]);

  // Cambio titolo EA: il club collegato e' del titolo precedente. Cerchiamo il
  // club nuovo (stesso nome e giocatori in comune) e lo proponiamo.
  const eaLegacy = isLegacyEaLink(team);
  const legacyOld = eaGameLabel(team.ea_game);
  const legacyCurrent = eaGameLabel(CURRENT_EA_GAME);
  const fetchSuccessor = async (refresh: boolean): Promise<EaSuccessorResult> => {
    const { data, error: invokeError } = await supabaseV2.functions.invoke("find-ea-club-successor", {
      body: { teamId: team.id, refresh },
    });
    if (invokeError) throw new Error(await invokeErrorMessage(invokeError));
    const r = data as any;
    if (r?.error) throw new Error(r.error);
    return { recommended: r?.recommended || null, candidates: (r?.candidates || []) as EaSuccessorCandidate[] };
  };
  const successorQuery = useQuery({
    queryKey: ["ea-successor", team.id, team.ea_club_id],
    queryFn: () => fetchSuccessor(false),
    enabled: canEdit && eaLegacy,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const retrySuccessor = useMutation({
    mutationFn: () => fetchSuccessor(true),
    onSuccess: (result) => queryClient.setQueryData(["ea-successor", team.id, team.ea_club_id], result),
  });
  const switchEaClub = useMutation({
    mutationFn: async (c: EaSuccessorCandidate) => {
      const { error: rpcError } = await supabaseV2.rpc("switch_ea_club", {
        _team_id: team.id,
        _ea_club_id: c.clubId,
        _ea_platform: c.platform,
      });
      if (rpcError) {
        if ((rpcError as any).code === "23505") {
          throw new Error(`${t("team.clubAlreadyOther", { id: c.clubId })} ${t("team.writeToMail")}`);
        }
        throw rpcError;
      }
      return c;
    },
    onSuccess: (c) => {
      setError(null);
      setEaClubId(c.clubId);
      setEaPlatform(c.platform);
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams-list"] });
      // Subito il sync: EA tiene solo le ultime partite, prima si scaricano
      // meno se ne perdono.
      runSync(false);
    },
    onError: (e: any) => setError(e?.message || String(e)),
  });

  const runSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchError(t("team.search2Chars"));
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    try {
      const { data, error: invokeError } = await supabaseV2.functions.invoke("search-ea-clubs", {
        body: { name: searchQuery.trim() }, // edge function cerca su entrambe le gen
      });
      if (invokeError) {
        let bodyMsg = "";
        try {
          const ctx: any = (invokeError as any).context;
          if (ctx?.body) {
            const txt = typeof ctx.body === "string" ? ctx.body : await ctx.body.text?.();
            if (txt) {
              try {
                const j = JSON.parse(txt);
                bodyMsg = j.error || j.message || txt;
              } catch {
                bodyMsg = txt;
              }
            }
          }
        } catch {
          // ignore
        }
        throw new Error(bodyMsg || invokeError.message);
      }
      const r = data as any;
      if (r?.error) throw new Error(r.error);
      const clubs = (r?.clubs || []) as any[];
      const takenSet = new Set<string>();
      if (clubs.length > 0) {
        const pairs = clubs.map((c) => ({ clubId: c.clubId, platform: c.platform }));
        const { data: takenRows } = await supabaseV2.rpc("taken_ea_clubs", {
          _pairs: pairs,
          _exclude_team_id: team.id,
        });
        for (const row of (takenRows || []) as any[]) {
          takenSet.add(`${row.ea_platform}:${row.ea_club_id}`);
        }
      }
      const annotated = clubs.map((c: any) => ({
        ...c,
        alreadyTaken: takenSet.has(`${c.platform}:${c.clubId}`),
      }));
      setSearchResults(annotated);
      if (annotated.length === 0) setSearchError(t("team.noClubsFound"));
    } catch (e: any) {
      console.error("[ea search]", e);
      setSearchError(e?.message || String(e));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const runSync = async (forceRefresh = false) => {
    setSyncing(true);
    setSyncMessage(forceRefresh ? t("team.refreshSyncStart") : t("team.syncStart"));
    try {
      const { data, error: invokeError } = await supabaseV2.functions.invoke("sync-ea-club", {
        body: { teamId: team.id, forceRefresh },
      });
      if (invokeError) {
        let bodyMsg = "";
        try {
          const ctx: any = (invokeError as any).context;
          if (ctx?.body) {
            const txt = typeof ctx.body === "string" ? ctx.body : await ctx.body.text?.();
            if (txt) {
              try {
                const j = JSON.parse(txt);
                bodyMsg = j.error || j.message || txt;
              } catch {
                bodyMsg = txt;
              }
            }
          }
        } catch {
          // ignore
        }
        throw new Error(bodyMsg || invokeError.message);
      }
      const r = data as any;
      if (r?.legacy) {
        setSyncMessage(t("team.eaLegacy.syncBlocked", {
          old: eaGameLabel(r.ea_game),
          current: eaGameLabel(r.current_game),
        }));
        return;
      }
      const errs = r?.errors || {};
      const errorList = Object.entries(errs).filter(([_, v]) => !!v).map(([k, v]) => `${k}: ${v}`);
      // Bulk roster import: dopo un sync riuscito creiamo uno stub player
      // per ogni member EA che non e' gia' in rosa (match per ea_account_handle).
      // Niente upsert/onConflict: l'index unique è partial e supabase-js
      // non sa generare ON CONFLICT con WHERE. Filtriamo lato client gli
      // handle gia' presenti e facciamo INSERT puro.
      let rosterImported = 0;
      try {
        const { data: members } = await supabaseV2.rpc("list_ea_members", {
          _team_id: team.id,
        });
        const memberRows = (members || []) as Array<{
          ea_persona: string;
          ea_name: string | null;
        }>;
        const { data: existing } = await supabaseV2
          .from("players")
          .select("ea_account_handle")
          .eq("team_id", team.id);
        const existingHandles = new Set(
          ((existing || []) as Array<{ ea_account_handle: string | null }>)
            .map((p) => (p.ea_account_handle || "").trim())
            .filter(Boolean),
        );
        const rosterPayload = memberRows
          .filter((m) => m.ea_persona && !existingHandles.has(m.ea_persona))
          .map((m) => ({
            team_id: team.id,
            first_name: ((m.ea_name || m.ea_persona) ?? "").trim() || m.ea_persona,
            last_name: "",
            ea_account_handle: m.ea_persona,
            ea_sports_id: m.ea_persona,
            status: "active" as const,
            is_guest: false,
          }));
        if (rosterPayload.length > 0) {
          const { data: inserted, error: insertErr } = await supabaseV2
            .from("players")
            .insert(rosterPayload)
            .select("id");
          if (insertErr) {
            console.warn("[ea sync] roster import fallito:", insertErr);
          } else {
            rosterImported = (inserted || []).length;
          }
        }
      } catch (e) {
        console.warn("[ea sync] roster import fallito:", e);
      }
      const rosterSuffix = rosterImported > 0 ? t("team.rosterSuffix", { count: rosterImported }) : "";
      if (errorList.length > 0) {
        setSyncMessage(t("team.syncWithWarn", {
          members: r.saved?.membersSaved || 0,
          matches: r.saved?.matchesSaved || 0,
          rosterSuffix,
          errors: errorList.join(" · "),
        }));
      } else {
        setSyncMessage(t("team.syncOk", {
          members: r.saved?.membersSaved || 0,
          matches: r.saved?.matchesSaved || 0,
          rosterSuffix,
        }));
      }
      queryClientForEa.invalidateQueries({ queryKey: ["ea-club", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["ea-members", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["ea-matches", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["ea-last-sync", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["players", team.id] });
    } catch (e: any) {
      console.error("[ea sync]", e);
      setSyncMessage(t("team.syncError", { message: e?.message || String(e) }));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    setName(team.name);
    setAbbreviation(team.abbreviation || "");
    setPrimaryColor(team.primary_color || "#005F7E");
    setLogoUrl(team.logo_url || "");
    setEaClubId(team.ea_club_id || "");
    setEaPlatform(team.ea_platform || "common-gen5");
  }, [team.id, team.name, team.abbreviation, team.primary_color, team.logo_url, team.ea_club_id, team.ea_platform]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = {
        name: name.trim() || team.name,
        abbreviation: abbreviation.trim().slice(0, 5).toUpperCase() || null,
        primary_color: primaryColor || null,
        logo_url: logoUrl.trim() || null,
        ea_club_id: eaClubId.trim() || null,
        ea_platform: eaPlatform || null,
      };
      const { data, error: updateError } = await supabaseV2
        .from("teams")
        .update(payload)
        .eq("id", team.id)
        .select("id");
      if (updateError) throw updateError;
      if (!data || data.length === 0) {
        throw new Error(t("team.noPermSave"));
      }
    },
    onSuccess: () => {
      setError(null);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams-list"] });
    },
    onError: (e: any) => {
      console.error("[team identity] save error", e);
      setError(e.message || String(e));
    },
  });

  // Elimina l'aggancio EA: azzera ea_club_id/ea_platform e pulisce gli
  // snapshot del club e dei membri, che descrivono il club attuale.
  // Le partite gia' scaricate restano: ognuna sa per quale club e' stata
  // presa (our_club_id), quindi non si mescola con quelle del club nuovo e
  // torna visibile appena se ne collega uno. Per buttare via anche quelle
  // c'e' l'azione dedicata purgeEaHistory.
  // Importa nella rosa i membri EA del club che non hanno ancora una riga
  // players (match per ea_account_handle). Per le squadre vecchie che hanno
  // gli ea_member_snapshots ma rosa non popolata: lo stesso lavoro che il
  // sync fa in coda, ma utilizzabile senza scaricare di nuovo dall'API EA.
  const importEaRoster = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabaseV2.rpc("import_ea_members_to_roster", {
        _team_id: team.id,
      });
      if (error) throw error;
      const row = (data as Array<{ imported: number; skipped: number }> | null)?.[0];
      return row || { imported: 0, skipped: 0 };
    },
    onSuccess: ({ imported, skipped }) => {
      setSyncMessage(t("team.eaImportDone", { imported, skipped }));
      queryClient.invalidateQueries({ queryKey: ["players", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["players-unlinked", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => setSyncMessage(t("team.syncError", { message: e?.message || String(e) })),
  });

  const unlinkEaClub = useMutation({
    mutationFn: async () => {
      const ops = await Promise.all([
        supabaseV2.from("ea_club_snapshots").delete().eq("team_id", team.id),
        supabaseV2.from("ea_member_snapshots").delete().eq("team_id", team.id),
      ]);
      const firstErr = ops.find((r) => r.error)?.error;
      if (firstErr) throw firstErr;

      const { data, error: updErr } = await supabaseV2
        .from("teams")
        .update({ ea_club_id: null, ea_platform: null })
        .eq("id", team.id)
        .select("id");
      if (updErr) throw updErr;
      if (!data || data.length === 0) {
        throw new Error(t("team.noPermUnlink"));
      }
    },
    onSuccess: () => {
      setError(null);
      setEaClubId("");
      setShowManualEntry(false);
      setSyncMessage(
        eaHistoryCount > 0
          ? t("team.linkRemoved", { count: eaHistoryCount })
          : t("team.linkRemovedNoHistory"),
      );
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams-list"] });
      queryClient.invalidateQueries({ queryKey: ["ea-club"] });
      queryClient.invalidateQueries({ queryKey: ["ea-members"] });
      queryClient.invalidateQueries({ queryKey: ["ea-matches"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => {
      console.error("[ea club unlink] error", e);
      setError(e.message || String(e));
    },
  });

  // Partite EA gia' scaricate: restano anche senza club collegato, e sono
  // l'unico posto dove vive lo storico dei titoli precedenti (EA non lo
  // ripubblica). Si cancellano solo da qui, su richiesta esplicita.
  const eaHistoryQuery = useQuery({
    queryKey: ["ea-history-count", team.id],
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("ea_match_snapshots")
        .select("id", { count: "exact", head: true })
        .eq("team_id", team.id);
      if (error) throw error;
      return count || 0;
    },
    enabled: canEdit,
    staleTime: 60_000,
  });
  const eaHistoryCount = eaHistoryQuery.data || 0;

  const purgeEaHistory = useMutation({
    mutationFn: async () => {
      const { error: delErr } = await supabaseV2.from("ea_match_snapshots").delete().eq("team_id", team.id);
      if (delErr) throw delErr;
      const { error: linkErr } = await supabaseV2
        .from("matches")
        .update({ ea_match_id: null })
        .eq("team_id", team.id)
        .not("ea_match_id", "is", null);
      if (linkErr) throw linkErr;
    },
    onSuccess: () => {
      setError(null);
      setSyncMessage(t("team.purgeDone", { count: eaHistoryCount }));
      queryClient.invalidateQueries({ queryKey: ["ea-history-count", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["ea-matches", team.id] });
      queryClientForEa.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (e: any) => setError(e?.message || String(e)),
  });

  // Salva solo l'aggancio EA (ea_club_id + ea_platform), senza richiedere
  // il click su "Salva modifiche". Triggerata da "Usa" sul risultato search.
  // Pre-check: il club EA non deve essere gia' associato a un team diverso da questo.
  const saveEaClub = useMutation({
    mutationFn: async ({ clubId, platform }: { clubId: string; platform: string }) => {
      const cleanedClub = clubId.trim();
      const cleanedPlatform = platform || "common-gen5";
      if (cleanedClub) {
        const { data: taken } = await supabaseV2.rpc("is_ea_club_taken", {
          _ea_club_id: cleanedClub,
          _ea_platform: cleanedPlatform,
          _exclude_team_id: team.id,
        });
        if (taken === true) {
          throw new Error(
            `${t("team.clubAlreadyOther", { id: cleanedClub })} ${t("team.writeToMailExplain")}`,
          );
        }
      }
      const { data, error: updateError } = await supabaseV2
        .from("teams")
        .update({
          ea_club_id: cleanedClub || null,
          ea_platform: cleanedPlatform,
        })
        .eq("id", team.id)
        .select("id");
      if (updateError) {
        if ((updateError as any).code === "23505") {
          throw new Error(
            `${t("team.clubAlreadyOther", { id: cleanedClub })} ${t("team.writeToMail")}`,
          );
        }
        throw updateError;
      }
      if (!data || data.length === 0) {
        throw new Error(t("team.noPermLink"));
      }
    },
    onSuccess: () => {
      setError(null);
      setSyncMessage(t("team.linkedSyncNow"));
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams-list"] });
    },
    onError: (e: any) => {
      console.error("[ea club save] error", e);
      setError(e.message || String(e));
    },
  });

  const onPickFile = async (file: File) => {
    setError(null);
    if (file.size > 5 * 1024 * 1024) {
      setError(t("team.fileTooBig"));
      return;
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 256, 0.85);
      setLogoUrl(dataUrl);
    } catch (e: any) {
      setError(t("team.imageReadError", { message: e.message || String(e) }));
    }
  };

  const previewLogo = logoUrl;
  const initials = (abbreviation || team.name?.slice(0, 3) || "EB").toUpperCase().slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("team.title")}</CardTitle>
        <CardDescription>
          {canEdit ? t("team.subtitleEdit") : t("team.subtitleView")}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        <div className="flex items-center gap-3 mb-4">
          {previewLogo ? (
            <img
              src={previewLogo}
              alt="logo"
              className="h-14 w-14 rounded-md object-cover border bg-card"
            />
          ) : (
            <div
              className="h-14 w-14 rounded-md grid place-items-center text-white font-mono text-[12px] font-semibold"
              style={{ background: primaryColor }}
            >
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-base truncate">{name}</p>
            <p className="text-xs text-muted-foreground font-mono">{team.slug}</p>
          </div>
        </div>
        {canEdit ? (
          <div className="grid lg:grid-cols-2 gap-x-8 gap-y-4">
            {/* Colonna SX: Identità */}
            <div className="space-y-3 min-w-0">
              <h3 className="font-semibold">{t("team.sectionIdentity")}</h3>
              <div className="space-y-1">
                <Label className="text-xs">{t("team.fieldName")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1 min-w-0">
                  <Label className="text-xs">{t("team.fieldShort")}</Label>
                  <Input
                    value={abbreviation}
                    onChange={(e) => setAbbreviation(e.target.value.toUpperCase())}
                    maxLength={5}
                    className="h-8 text-sm font-mono uppercase"
                    placeholder={t("team.shortPh")}
                  />
                </div>
                <div className="space-y-1 min-w-0">
                  <Label className="text-xs">{t("team.fieldColor")}</Label>
                  <div className="flex gap-1.5 min-w-0">
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-8 w-10 shrink-0 rounded border bg-card cursor-pointer"
                    />
                    <Input
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-8 text-sm font-mono flex-1 min-w-0"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-1 min-w-0">
                <Label className="text-xs">{t("team.fieldLogo")}</Label>
                <Input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder={t("team.logoPh")}
                  className="h-8 text-sm"
                />
                <div className="flex gap-1.5 items-center flex-wrap">
                  <input
                    type="file"
                    accept="image/*"
                    className="text-xs text-muted-foreground max-w-full file:mr-3 file:h-9 file:rounded-full file:border-0 file:bg-raised file:px-4 file:text-sm file:text-foreground"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onPickFile(f);
                    }}
                  />
                  {logoUrl && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLogoUrl("")}>
                      {t("team.removeLogo")}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Colonna DX: FC World Pro Clubs */}
            <div className="space-y-3 min-w-0">
              <h3 className="font-semibold">{t("team.sectionEa")}</h3>

              {/* Club del titolo EA precedente: proposta del club nuovo. */}
              {eaLegacy && (
                <div className="rounded-xl bg-amber-50 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 space-y-1">
                      <p className="text-[12.5px] font-semibold">
                        {t("team.eaLegacy.title", { old: legacyOld, current: legacyCurrent })}
                      </p>
                      <p className="text-[11.5px] text-muted-foreground">
                        {t("team.eaLegacy.body", { old: legacyOld, current: legacyCurrent })}
                      </p>
                    </div>
                  </div>
                  {(successorQuery.isLoading || retrySuccessor.isPending) && (
                    <p className="text-xs text-muted-foreground">{t("team.eaLegacy.searching", { current: legacyCurrent })}</p>
                  )}
                  {!successorQuery.isLoading && !retrySuccessor.isPending && (successorQuery.error || retrySuccessor.error) && (
                    <p className="text-xs text-rose-600">
                      {t("team.eaLegacy.searchError", {
                        message: ((retrySuccessor.error || successorQuery.error) as Error)?.message || "",
                      })}
                    </p>
                  )}
                  {successorQuery.data && !retrySuccessor.isPending && successorQuery.data.candidates.length === 0 && (
                    <p className="text-xs text-muted-foreground">{t("team.eaLegacy.none", { current: legacyCurrent })}</p>
                  )}
                  {successorQuery.data && !retrySuccessor.isPending && successorQuery.data.candidates.length > 0 && (
                    <div className="space-y-1">
                      {successorQuery.data.candidates.slice(0, 4).map((c) => (
                        <div
                          key={`${c.platform}:${c.clubId}`}
                          className="flex items-center gap-2 px-2 py-1.5 rounded border bg-card text-sm"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate flex items-center gap-1.5">
                              {c.name}
                              {successorQuery.data?.recommended === c.clubId && (
                                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                  {t("team.eaLegacy.recommended")}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {t("team.id")} {c.clubId}
                              {c.gamesPlayed != null ? ` · ${c.gamesPlayed} ${t("team.matches")}` : ""}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {c.common.length > 0
                                ? t("team.eaLegacy.common", { count: c.common.length, names: c.common.slice(0, 4).join(", ") })
                                : t("team.eaLegacy.noCommon")}
                            </p>
                            {c.alreadyTaken && (
                              <p className="text-xs text-amber-700 mt-0.5">{t("team.alreadyAssociated")}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            variant={successorQuery.data?.recommended === c.clubId ? "default" : "outline"}
                            disabled={switchEaClub.isPending || syncing || c.alreadyTaken}
                            onClick={() => switchEaClub.mutate(c)}
                          >
                            {switchEaClub.isPending && switchEaClub.variables?.clubId === c.clubId
                              ? t("team.savingShort")
                              : t("team.eaLegacy.link")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                      onClick={() => setSearchOpen(true)}
                    >
                      {t("team.eaLegacy.searchByName")}
                    </button>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors disabled:opacity-50"
                      disabled={retrySuccessor.isPending || successorQuery.isLoading}
                      onClick={() => retrySuccessor.mutate()}
                    >
                      {t("team.eaLegacy.retry")}
                    </button>
                  </div>
                </div>
              )}

              {/* Default state (nessun aggancio + nessun manual entry):
                  bottone search prominente. Il link manuale e' spostato in
                  fondo al box (sotto la search), cosi' resta accessibile
                  anche quando la search e' aperta. */}
              {!team.ea_club_id && !showManualEntry && !searchOpen && (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setSearchOpen(true)}
                  className="w-full sm:w-auto"
                >
                  <Search className="h-4 w-4" />
                  {t("team.searchCta")}
                </Button>
              )}

              {/* Box ID + piattaforma: visibile se aggancio gia' fatto o utente chiede manual.
                  Su mobile 50/50 (la colonna piattaforma con 140px tagliava "Next gen"). */}
              {(team.ea_club_id || showManualEntry) && (
                <div className="grid grid-cols-2 sm:grid-cols-[1fr_160px] gap-2">
                  <div className="space-y-1 min-w-0">
                    <Label className="text-xs">{t("team.eaClubId")}</Label>
                    <Input
                      value={eaClubId}
                      onChange={(e) => setEaClubId(e.target.value)}
                      placeholder={t("team.eaClubIdPh")}
                      className="h-8 text-sm font-mono"
                    />
                  </div>
                  <div className="space-y-1 min-w-0">
                    <Label className="text-xs">{t("team.platform")}</Label>
                    <Select value={eaPlatform} onValueChange={setEaPlatform}>
                      <SelectTrigger className="h-8 text-sm min-w-0">
                        <SelectValue className="truncate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="common-gen5">{t("team.nextGen")}</SelectItem>
                        <SelectItem value="common-gen4">{t("team.oldGen")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Manual entry attivo ma nessun aggancio ancora: bottone Salva ID + link torna alla ricerca */}
              {!team.ea_club_id && showManualEntry && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    disabled={saveEaClub.isPending || !eaClubId.trim()}
                    onClick={() => {
                      setSyncMessage(t("team.linkInProgress"));
                      saveEaClub.mutate({ clubId: eaClubId.trim(), platform: eaPlatform });
                    }}
                  >
                    {saveEaClub.isPending ? t("team.savingShort") : t("team.saveAndLink")}
                  </Button>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                    onClick={() => {
                      setShowManualEntry(false);
                      setEaClubId("");
                    }}
                  >
                    {t("team.cancelToSearch")}
                  </button>
                </div>
              )}

              {/* Aggancio gia' fatto: bottoni sync + cambia club + elimina */}
              {team.ea_club_id && !searchOpen && (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-muted-foreground">
                    {t("team.autoSyncNote")}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!eaLegacy && (
                      <Button size="sm" onClick={() => runSync(false)} disabled={syncing} className="btn-lift">
                        {syncing ? t("team.syncing") : t("team.syncNow")}
                      </Button>
                    )}
                    {/* Azioni avanzate: forza refresh upstream (raro: usare se i
                        dati sembrano stantii) + import rosa one-shot da snapshot. */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={syncing || importEaRoster.isPending}
                          title={t("team.advancedActionsTitle")}
                          aria-label={t("team.advancedActionsTitle")}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="max-w-xs">
                        <DropdownMenuItem
                          onClick={() => runSync(true)}
                          className="flex-col items-start gap-0.5"
                        >
                          <span className="font-medium">{t("team.refreshSync")}</span>
                          <span className="text-xs text-muted-foreground leading-snug whitespace-normal">
                            {t("team.refreshSyncHint")}
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => importEaRoster.mutate()}
                          className="flex-col items-start gap-0.5"
                        >
                          <span className="font-medium">
                            {importEaRoster.isPending ? t("team.eaImporting") : t("team.eaImport")}
                          </span>
                          <span className="text-xs text-muted-foreground leading-snug whitespace-normal">
                            {t("team.eaImportHint")}
                          </span>
                        </DropdownMenuItem>
                        {eaHistoryCount > 0 && (
                          <DropdownMenuItem
                            onClick={() => {
                              const ok = window.confirm(t("team.confirmPurge", { count: eaHistoryCount }));
                              if (ok) purgeEaHistory.mutate();
                            }}
                            className="flex-col items-start gap-0.5 text-rose-600 focus:text-rose-600"
                          >
                            <span className="font-medium">
                              {purgeEaHistory.isPending ? t("team.purging") : t("team.purgeHistory")}
                            </span>
                            <span className="text-xs text-muted-foreground leading-snug whitespace-normal">
                              {t("team.purgeHistoryHint", { count: eaHistoryCount })}
                            </span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSearchOpen(true)}
                      title={t("team.changeClubTitle")}
                    >
                      <Search className="h-3.5 w-3.5" />
                      {t("team.changeClub")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      disabled={unlinkEaClub.isPending || syncing}
                      onClick={() => {
                        const ok = window.confirm(t("team.confirmUnlink"));
                        if (ok) unlinkEaClub.mutate();
                      }}
                      title={t("team.removeLinkTitle")}
                    >
                      {unlinkEaClub.isPending ? t("team.removingShort") : t("team.removeLink")}
                    </Button>
                  </div>
                  {syncMessage && (
                    <p className="text-xs text-muted-foreground">{syncMessage}</p>
                  )}
                </div>
              )}

              {/* Search box: visibile quando searchOpen */}
              {searchOpen && (
                <div className="rounded-2xl bg-background p-2 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-muted-foreground">
                      {t("team.searchTitle")}
                    </p>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setSearchOpen(false);
                        setSearchResults([]);
                        setSearchError(null);
                      }}
                    >
                      {t("team.close")}
                    </button>
                  </div>
                  <form
                    className="flex gap-1.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      runSearch();
                    }}
                  >
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("team.searchPh")}
                      className="h-8 text-sm flex-1"
                      autoFocus
                    />
                    <Button type="submit" size="sm" className="h-8" disabled={searchLoading}>
                      {searchLoading ? t("team.searching") : t("team.search")}
                    </Button>
                  </form>
                  {searchError && (
                    <div className="text-xs text-rose-600">{searchError}</div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="max-h-56 overflow-auto space-y-1">
                      {searchResults.map((c) => (
                        <div
                          key={`${c.platform || "any"}:${c.clubId}`}
                          className={
                            c.alreadyTaken
                              ? "flex items-center gap-2 px-2 py-1.5 rounded border bg-muted/30 text-sm opacity-70"
                              : "flex items-center gap-2 px-2 py-1.5 rounded border bg-card text-sm"
                          }
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate flex items-center gap-1.5">
                              {c.name}
                              {c.platform && (
                                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {c.platform === "common-gen5" ? t("team.nextGenShort") : c.platform === "common-gen4" ? t("team.oldGenShort") : c.platform}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {t("team.id")} {c.clubId}
                              {c.currentDivision != null ? ` · ${t("team.div")} ${c.currentDivision}` : ""}
                              {c.gamesPlayed != null ? ` · ${c.gamesPlayed} ${t("team.matches")}` : ""}
                              {c.wins != null ? ` · ${c.wins}${t("team.winsShort")}` : ""}
                            </p>
                            {c.alreadyTaken && (
                              <p className="text-xs text-amber-700 mt-0.5">
                                {t("team.alreadyAssociated")}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant={c.alreadyTaken ? "outline" : "default"}
                            className="h-7 text-xs"
                            disabled={saveEaClub.isPending || c.alreadyTaken}
                            onClick={() => {
                              if (c.alreadyTaken) return;
                              const platform = c.platform || eaPlatform;
                              setEaClubId(c.clubId);
                              setEaPlatform(platform);
                              setSearchOpen(false);
                              setSearchResults([]);
                              setShowManualEntry(true);
                              setSyncMessage(t("team.linkInProgress"));
                              saveEaClub.mutate({ clubId: c.clubId, platform });
                            }}
                          >
                            {c.alreadyTaken ? t("team.alreadyAssociatedBtn") : (saveEaClub.isPending ? t("team.searching") : t("team.useBtn"))}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Storico EA rimasto da un club scollegato: torna visibile
                  appena se ne collega uno nuovo, oppure si butta via da qui. */}
              {!team.ea_club_id && eaHistoryCount > 0 && (
                <div className="rounded-2xl bg-background p-2 space-y-1">
                  <p className="text-[11.5px] text-muted-foreground">
                    {t("team.historyKept", { count: eaHistoryCount })}
                  </p>
                  <button
                    type="button"
                    className="text-xs text-rose-600 hover:text-rose-700 underline-offset-2 hover:underline transition-colors disabled:opacity-50"
                    disabled={purgeEaHistory.isPending}
                    onClick={() => {
                      const ok = window.confirm(t("team.confirmPurge", { count: eaHistoryCount }));
                      if (ok) purgeEaHistory.mutate();
                    }}
                  >
                    {purgeEaHistory.isPending ? t("team.purging") : t("team.purgeHistory")}
                  </button>
                </div>
              )}

              {/* Link "Conosci gia' l'ID": in fondo al box, accessibile sia
                  in default sia mentre la search e' aperta. Si nasconde solo
                  dopo l'aggancio o quando l'utente attiva il manual entry. */}
              {!team.ea_club_id && !showManualEntry && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                  onClick={() => setShowManualEntry(true)}
                >
                  {t("team.knowIdHint")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <PriorityTile label={t("team.abbreviationLabel")} value={team.abbreviation || "-"} />
            <PriorityTile label={t("team.primaryLabel")} value={team.primary_color || "-"} />
          </div>
        )}
      </CardContent>
      {canEdit && (
        <CardFooter className="border-t bg-muted/20 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[12px] min-w-0 flex-1">
            {error && (
              <span className="text-rose-700 font-medium truncate">{error}</span>
            )}
            {!error && savedFlash && (
              <span className="text-emerald-600 font-medium">{t("team.saved")}</span>
            )}
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? t("team.savingShort") : t("team.saveChanges")}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
